import type * as THREE from "three";

/**
 * Camera-preset tween: hero/side/interior preset switches ease the camera
 * from its current pose to the preset instead of teleporting, so the
 * user keeps their spatial bearing (especially on the jump to the
 * interior view).
 *
 * Constraints that shaped this implementation (mirrors revealAnimation):
 * - The viewer runs `frameloop="demand"`, so the loop must call
 *   `invalidate()` every frame while active AND stop invalidating the
 *   instant it finishes — a tween that never ends pins the render loop.
 * - Playwright visual baselines screenshot ~750ms after viewer-ready
 *   and jsdom component tests assert synchronously; the tween is
 *   cosmetic, so it is skipped under automation and reduced-motion —
 *   callers apply the final pose instantly instead.
 * - Cancelling (user takeover, unmount, preset change mid-flight)
 *   restores the exact target pose so the camera never stalls halfway.
 */

export interface CameraTweenTiming {
	durationMs: number;
}

export const CAMERA_TWEEN_TIMING: CameraTweenTiming = {
	durationMs: 700,
};

/** Hard cap so a stalled rAF clock can never keep the loop invalidating. */
export const CAMERA_TWEEN_MAX_MS = 1200;

function easeInOutCubic(t: number): number {
	return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * Eased progress (0..1) at `elapsedMs`. 0 = start pose, 1 = target
 * pose. Clamped at both ends.
 */
export function getTweenProgress(
	elapsedMs: number,
	durationMs: number = CAMERA_TWEEN_TIMING.durationMs,
): number {
	if (durationMs <= 0) return 1;
	const t = elapsedMs / durationMs;
	if (t <= 0) return 0;
	if (t >= 1) return 1;
	return easeInOutCubic(t);
}

export function shouldTweenCamera({
	// webdriver: Playwright screenshots ~750ms after viewer-ready.
	// jsdom: component tests assert camera pose right after a preset
	// switch — the tween is cosmetic, so it never runs under either.
	isAutomated = typeof navigator !== "undefined" &&
		(!!navigator.webdriver || /jsdom/i.test(navigator.userAgent ?? "")),
	prefersReducedMotion = typeof window !== "undefined" &&
		typeof window.matchMedia === "function" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches,
}: {
	isAutomated?: boolean;
	prefersReducedMotion?: boolean;
} = {}): boolean {
	return !isAutomated && !prefersReducedMotion;
}

export interface CameraTweenTargets {
	/** Camera position, mutated in place each frame. */
	cameraPosition: THREE.Vector3;
	/** OrbitControls target, mutated in place each frame. */
	controlsTarget: THREE.Vector3;
}

export interface CameraTweenGoal {
	position: THREE.Vector3;
	target: THREE.Vector3;
}

export interface CameraTweenHooks {
	/** Called every frame after the pose is applied (demand-loop kick). */
	invalidate: () => void;
	/** Push the controls' internal state after target/position change. */
	updateControls?: () => void;
	/** Fired once when the tween begins moving (idle sensor: camera busy). */
	onStart?: () => void;
	/** Fired once when the tween settles or is cancelled (idle sensor: re-arm). */
	onEnd?: () => void;
}

/**
 * Lerps `targets` from their current values to `goal` over `durationMs`
 * via rAF, calling `invalidate()` each frame. Returns a cancel function.
 * Both natural completion and cancellation snap to the exact goal pose
 * and fire `onEnd` exactly once, so callers can fire-and-forget but MUST
 * cancel before unmount / the next preset switch.
 */
export function playCameraTween(
	targets: CameraTweenTargets,
	goal: CameraTweenGoal,
	hooks: CameraTweenHooks,
	timing: CameraTweenTiming = CAMERA_TWEEN_TIMING,
): () => void {
	const applyGoal = () => {
		targets.cameraPosition.copy(goal.position);
		targets.controlsTarget.copy(goal.target);
		hooks.updateControls?.();
	};

	if (typeof requestAnimationFrame !== "function") {
		applyGoal();
		hooks.invalidate();
		return () => {};
	}

	const fromPosition = targets.cameraPosition.clone();
	const fromTarget = targets.controlsTarget.clone();
	const durationMs = Math.min(timing.durationMs, CAMERA_TWEEN_MAX_MS);
	const startedAt = performance.now();
	let frame = 0;
	let done = false;

	hooks.onStart?.();

	const finish = () => {
		if (done) return;
		done = true;
		cancelAnimationFrame(frame);
		applyGoal();
		hooks.invalidate();
		hooks.onEnd?.();
	};

	const tick = () => {
		if (done) return;
		const elapsed = performance.now() - startedAt;
		// Hard cap: even if the rAF clock stalls, elapsed eventually
		// exceeds the cap and the loop stops invalidating.
		if (elapsed >= durationMs || elapsed >= CAMERA_TWEEN_MAX_MS) {
			finish();
			return;
		}
		const progress = getTweenProgress(elapsed, durationMs);
		targets.cameraPosition.lerpVectors(fromPosition, goal.position, progress);
		targets.controlsTarget.lerpVectors(fromTarget, goal.target, progress);
		hooks.updateControls?.();
		hooks.invalidate();
		frame = requestAnimationFrame(tick);
	};
	frame = requestAnimationFrame(tick);

	return finish;
}
